import { KeyboardEvent, useState } from 'react';
import { Check, Loader2, Pencil, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '@Helpers';

import { Input } from '@Components/ui-v2/input';
import { bopsApi } from '@Network';
import { getErrorDetail, SCHEDULE_NAME_MAX_LENGTH } from '../constants';

interface ScheduleNameEditorProps {
  scheduleId: string;
  name: string;
  canManage: boolean;
  onRenamed: () => Promise<void>;
}

const ScheduleNameEditor = ({ scheduleId, name, canManage, onRenamed }: ScheduleNameEditorProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const trimmedDraft = draft?.trim() ?? '';

  const handleSave = async () => {
    if (trimmedDraft === '' || isSaving) return;
    if (trimmedDraft === name) {
      setDraft(null);
      return;
    }
    setIsSaving(true);
    try {
      await bopsApi.updateSchedule(scheduleId, { name: trimmedDraft });
    } catch (error) {
      showErrorToast(getErrorDetail(error) ?? t('Schedule.Detail.Rename.Error'));
      setIsSaving(false);
      return;
    }
    try {
      await onRenamed();
    } finally {
      setDraft(null);
      setIsSaving(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSave().catch(() => {});
    } else if (event.key === 'Escape') {
      setDraft(null);
    }
  };

  if (draft === null) {
    return (
      <div className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-xl font-bold text-gray-900">{name}</span>
        {canManage && (
          <button
            type="button"
            aria-label={t('Schedule.Detail.Rename.Edit')}
            onClick={() => setDraft(name)}
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700">
            <Pencil size={14} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <Input
        autoFocus
        value={draft}
        maxLength={SCHEDULE_NAME_MAX_LENGTH}
        disabled={isSaving}
        aria-label={t('Schedule.Detail.Rename.Input')}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        className="h-9 w-80 text-base font-semibold"
      />
      {isSaving ? (
        <Loader2 size={16} className="animate-spin text-gray-400" />
      ) : (
        <>
          <button
            type="button"
            aria-label={t('Schedule.Detail.Rename.Save')}
            disabled={trimmedDraft === ''}
            onClick={() => handleSave().catch(() => {})}
            className="flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-40">
            <Check size={16} />
          </button>
          <button
            type="button"
            aria-label={t('Schedule.Detail.Rename.Cancel')}
            onClick={() => setDraft(null)}
            className="flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900">
            <X size={16} />
          </button>
        </>
      )}
    </div>
  );
};

export default ScheduleNameEditor;
